import { useTranslation } from "react-i18next";
import { useDocument } from "~/api/v2/useDocument";
import { useDocumentRelease } from "~/api/v2/useDocumentRelease";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "~/atoms/dialog";
import { CenterSpinner } from "~/atoms/spinner";
import NoResult from "~/molecules/NoResult";
import { DocumentFieldEditForm } from "./DocumentFieldEditForm";
import { ReleaseFieldEditForm } from "./ReleaseFieldEditForm";
import { getEditableFieldLabel } from "./columnRegistry";
import { isReleaseEditableField } from "~/helpers/libraryEditing";
import type {
  DocumentEditableField,
  ReleaseEditableField,
} from "~/api/v2/requests";
import type { ActiveFieldEdit } from "~/helpers/libraryTypes";

interface FieldEditDialogProps {
  activeEdit: ActiveFieldEdit | null;
  onOpenChange: (isOpen: boolean) => void;
  onSaved: () => Promise<void>;
}

export const FieldEditDialog = ({
  activeEdit,
  onOpenChange,
  onSaved,
}: FieldEditDialogProps) => {
  const { t } = useTranslation();
  const isReleaseField = activeEdit
    ? isReleaseEditableField(activeEdit.field)
    : false;
  const {
    data: document,
    isLoading,
    error,
    mutate: mutateDocument,
  } = useDocument(isReleaseField ? undefined : activeEdit?.documentId);
  const {
    data: release,
    isLoading: isReleaseLoading,
    error: releaseError,
    mutate: mutateRelease,
  } = useDocumentRelease(isReleaseField ? activeEdit?.releaseId : undefined);
  const isDialogLoading = isReleaseField ? isReleaseLoading : isLoading;
  const dialogError = isReleaseField ? releaseError : error;
  const handleSaved = async () => {
    if (isReleaseField) {
      await Promise.all([onSaved(), mutateRelease()]);
      return;
    }

    await Promise.all([onSaved(), mutateDocument()]);
  };

  return (
    <Dialog open={activeEdit !== null} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>
            {activeEdit
              ? `${t(($) => $.commons.buttons.edit)} ${getEditableFieldLabel(activeEdit.field, t)}`
              : ""}
          </DialogTitle>
        </DialogHeader>

        {!activeEdit ? null : isDialogLoading ? (
          <CenterSpinner />
        ) : dialogError ? (
          <NoResult message={t(($) => $.commons.errors.noDocument)} />
        ) : isReleaseField ? (
          release ? (
            <ReleaseFieldEditForm
              release={release}
              field={activeEdit.field as ReleaseEditableField}
              onCancel={() => onOpenChange(false)}
              onSaved={handleSaved}
            />
          ) : (
            <NoResult message={t(($) => $.commons.errors.noDocument)} />
          )
        ) : !document ? (
          <NoResult message={t(($) => $.commons.errors.noDocument)} />
        ) : (
          <DocumentFieldEditForm
            key={activeEdit.field}
            document={document}
            field={activeEdit.field as DocumentEditableField}
            onCancel={() => onOpenChange(false)}
            onSaved={handleSaved}
          />
        )}
      </DialogContent>
    </Dialog>
  );
};
